import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@aix/ui"

import type { SeoMeasurementCohortKey } from "../../../aixinternal-api/src/service/seo/scoreboard"
import { trpc } from "../lib/trpc"
import { healthBadgeVariant } from "./seoBoardHelpers"

interface SEOScoreboardTabProps {
  productKey: string
}

const COHORT_LABELS: Record<SeoMeasurementCohortKey, string> = {
  "day-2": "0–2 days",
  "day-7": "3–7 days",
  "day-14": "8–14 days",
  "day-28": "15+ days",
} as const

export default function SEOScoreboardTab({ productKey }: SEOScoreboardTabProps) {
  const { data, isLoading, error, refetch, isRefetching } = trpc.seo.metrics.scoreboard.useQuery({ productKey })

  const clusterEntries = data ? Object.entries(data.clusters) : []
  const healthEntries = data ? Object.entries(data.healthStates) : []
  const indexationEntries = data ? Object.entries(data.indexationStates) : []
  const cohortEntries = data ? Object.entries(data.cohorts) : []
  const perPage = data ? Object.entries(data.perPageHealth) : []

  return (
    <div className="h-full overflow-auto p-4">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Latest observation per managed page. Missing data remains unknown instead of being reported as zero.
          </p>
          <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
            Refresh
          </Button>
        </div>

        {error ? (
          <div className="rounded border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            {error.message}
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            <div>Loading…</div>
          </div>
        ) : !data || data.managedPages === 0 ? (
          <div className="rounded border-2 border-border bg-background p-8 text-center text-sm text-muted-foreground">
            No managed SEO pages exist for this product yet.
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                label="Managed pages"
                value={data.managedPages}
                detail={`${data.unpublishedPages} unpublished`}
              />
              <SummaryCard label="Measured pages" value={data.pageCount} detail={`${data.unmeasuredPages} unknown`} />
              <SummaryCard
                label="Data freshness"
                value={data.freshness}
                detail={
                  data.observedAt
                    ? `Oldest observation ${data.observedAt} · ${data.observationAgeDays ?? 0}d old`
                    : "Never observed"
                }
              />
              <SummaryCard
                label="Indexed pages"
                value={data.indexationStates.indexed ?? 0}
                detail={`${data.managedPages - (data.indexationStates.indexed ?? 0)} not confirmed`}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Indexation state</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {indexationEntries.map(([state, count]) => (
                  <Badge key={state} variant={state === "indexed" ? "success" : "secondary"}>
                    {state}: {count}
                  </Badge>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>2 / 7 / 14 / 28-day review cohorts</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-1">Published age</th>
                      <th className="py-1">Pages</th>
                      <th className="py-1">Measured</th>
                      <th className="py-1">Indexed</th>
                      <th className="py-1">Impressions</th>
                      <th className="py-1">Clicks</th>
                      <th className="py-1">Signups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cohortEntries.map(([cohort, roll]) => (
                      <tr key={cohort} className="border-t border-border">
                        <td className="py-1">{COHORT_LABELS[cohort as SeoMeasurementCohortKey]}</td>
                        <td className="py-1">{roll.pages}</td>
                        <td className="py-1">{roll.measuredPages}</td>
                        <td className="py-1">{roll.indexedPages}</td>
                        <td className="py-1">{roll.impressions.toLocaleString()}</td>
                        <td className="py-1">{roll.clicks.toLocaleString()}</td>
                        <td className="py-1">{roll.signups.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {data.pageCount === 0 ? (
              <div className="rounded border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                The pages are managed, but no managed-page Search Console or conversion observation has been ingested
                yet.
              </div>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Measured-page health ({data.pageCount})</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {healthEntries.map(([health, count]) => (
                      <Badge key={health} variant={healthBadgeVariant(health)}>
                        {health}: {count}
                      </Badge>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Per-cluster rollup</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="py-1">Cluster</th>
                          <th className="py-1">Measured</th>
                          <th className="py-1">Impressions</th>
                          <th className="py-1">Clicks</th>
                          <th className="py-1">Signups</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clusterEntries.map(([cluster, roll]) => (
                          <tr key={cluster} className="border-t border-border">
                            <td className="py-1">{cluster}</td>
                            <td className="py-1">{roll.pages}</td>
                            <td className="py-1">{roll.impressions.toLocaleString()}</td>
                            <td className="py-1">{roll.clicks.toLocaleString()}</td>
                            <td className="py-1">{roll.signups.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Per-page health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="py-1">Slug</th>
                          <th className="py-1">Health</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perPage.map(([slug, health]) => (
                          <tr key={slug} className="border-t border-border">
                            <td className="py-1 font-mono text-xs">{slug}</td>
                            <td className="py-1">
                              <Badge variant={healthBadgeVariant(health)}>{health}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function SummaryCard({ label, value, detail }: { label: string; value: number | string; detail: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="mt-1 block text-2xl font-semibold tabular-nums">{value}</span>
        <span className="mt-1 block text-xs text-muted-foreground">{detail}</span>
      </CardContent>
    </Card>
  )
}
